import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Backdrop, Fade, ModalUnstyledProps, Modal as MUIModal,
  Stack,
} from '@mui/material';

import {
  BaseColorKeys, NotifierColorKeys, PureColorKeys, SubjectColorKeys,
} from '../../theme';
import Button from '../Button';

import {
  ButtonBar, ChildBox, CloseIconButton, Container, Description, Title, TitleContainer,
} from './index.styles';

export type ModalButton = {
  label: string,
  onClick?: () => void,
  color?: BaseColorKeys | NotifierColorKeys | PureColorKeys,
  closeOnClick?: boolean,
  loading?: boolean,
  disabled?: boolean,
}

interface ModalProps extends Omit<ModalUnstyledProps, 'children'>{
  open: boolean,
  handleClose?: (event?: object, reason?: string) => void
  hideCloseIcon?: boolean,
  title?: string,
  titleColor?: BaseColorKeys | SubjectColorKeys | NotifierColorKeys,
  description?: string,
  buttons?: ModalButton[],
  children?: React.ReactElement,
  scrollY?: boolean,
}

const Modal = ({
  open = false,
  handleClose = async () => {},
  hideCloseIcon = false,
  title,
  children = <div />,
  description,
  buttons = [],
  titleColor = 'gray',
  scrollY,
  ...props
}: ModalProps) => (
  <MUIModal
    open={open}
    onClose={handleClose}
    closeAfterTransition
    BackdropComponent={Backdrop}
    BackdropProps={{
      timeout: 500,
    }}
    {...props}
  >
    <Fade in={open}>
      <Container
        $scrollY={scrollY}
      >
        {!hideCloseIcon && (
          <CloseIconButton onClick={handleClose}>
            <CloseIcon />
          </CloseIconButton>
        )}
        {title && (
          <TitleContainer color={titleColor}>
            <Title variant="h5">
              {title}
            </Title>
          </TitleContainer>
        )}
        {description && (
          <Description>
            <div>
              {description.split('\n').map((line) => (
                <p key={`line-${line}`}>{line}</p>
              ))}
            </div>
          </Description>
        )}
        {children && (
          <ChildBox $scrollY={scrollY}>
            {React.cloneElement(children)}
          </ChildBox>
        )}
        {buttons.length > 0 && (
          <ButtonBar>
            <Stack
              direction="row"
              spacing={1}
            >
              {buttons.map((button) => (
                <Button
                  key={`${button.label}${button?.color}`}
                  loading={button?.loading}
                  onClick={async () => {
                    if (button.onClick) await button.onClick();
                    if (button?.closeOnClick) await handleClose();
                  }}
                  color={button?.color}
                  disabled={button?.disabled}
                  size="small"
                  variant="text"
                  allCaps
                >
                  {button.label}
                </Button>
              ))}
            </Stack>
          </ButtonBar>
        )}
      </Container>
    </Fade>
  </MUIModal>
);

export default Modal;
